import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTutorial } from '../contexts/TutorialContext';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import './TownDetailsModal.css';

interface Town {
  id: number;
  name: string;
  description: string;
  townType: string;
  worldX: number;
  worldY: number;
  bossName: string;
  isLiberated: boolean;
  standing: number;
  level: number;
  lastActivityDate: string | null;
  canFastTravel: boolean;
}

interface Quest {
  type: string;
  description: string;
  standingReward: number;
  requiredCount: number;
  requiredMaterialType?: string;
}

interface Bonus {
  slot: number;
  tier: number;
  requiredLevel: number;
  bonusType: string;
  bonusName: string;
  description: string;
  bonusValue: number;
  isActive: boolean;
  isLocked: boolean;
}

interface TownDetailsModalProps {
  townId: number;
  onClose: () => void;
  onFastTravel?: (townId: number) => void;
}

const TownDetailsModal: React.FC<TownDetailsModalProps> = ({ townId, onClose, onFastTravel }) => {
  const { checkAndStartTutorial } = useTutorial();
  const [town, setTown] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [completingQuest, setCompletingQuest] = useState(false);
  const [questProgress, setQuestProgress] = useState<any>(null);

  useEffect(() => {
    loadTownDetails();
    loadQuestProgress();
  }, [townId]);

  // Auto-start town tutorial when first viewing a town
  useEffect(() => {
    if (town && !loading) {
      checkAndStartTutorial('town-system-basics');
    }
  }, [town, loading, checkAndStartTutorial]);

  const loadQuestProgress = async () => {
    try {
      const response = await makeAuthenticatedRequest('/town/quest-progress');
      if (response.ok) {
        const data = await response.json();
        setQuestProgress(data);
      }
    } catch (err) {
      console.error('Failed to load quest progress:', err);
    }
  };

  const loadTownDetails = async () => {
    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest(`/town/${townId}`);

      if (response.ok) {
        const data = await response.json();
        setTown(data);
      } else {
        setError('Failed to load town details');
      }
    } catch (err) {
      setError('Error loading town details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteQuest = async (quest: Quest) => {
    if (!town.progress.isLiberated) {
      setError('Town must be liberated first! Defeat the boss to liberate this town.');
      return;
    }

    try {
      setCompletingQuest(true);
      const response = await makeAuthenticatedRequest(`/town/${townId}/quest/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          questType: quest.type,
          questDescription: quest.description,
          standingReward: quest.standingReward,
          requiredCount: quest.requiredCount,
          requiredMaterialType: quest.requiredMaterialType
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Quest Complete! +${result.standingGained} standing\nNew Standing: ${result.newStanding}\nTown Level: ${result.newLevel}`);
        await loadTownDetails(); // Reload to show updated progress
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to complete quest');
      }
    } catch (err) {
      setError('Error completing quest');
      console.error(err);
    } finally {
      setCompletingQuest(false);
    }
  };

  const handleFastTravel = async () => {
    if (!onFastTravel) return;

    if (!town.progress.canFastTravel) {
      setError('Town must be liberated (Level >= 1) to fast travel');
      return;
    }

    const confirmed = window.confirm(`Fast travel to ${town.town.name}? This will cost 25% of normal travel points.`);
    if (confirmed) {
      onFastTravel(townId);
    }
  };

  const getTownIcon = (type: string) => {
    switch (type) {
      case 'Mining': return '⛏️';
      case 'Forest': return '🌲';
      case 'Trading': return '🏪';
      case 'Military': return '⚔️';
      case 'Magical': return '🔮';
      default: return '🏰';
    }
  };

  const getStandingColor = (standing: number) => {
    if (standing >= 900) return '#ffd700'; // Gold
    if (standing >= 700) return '#9d4edd'; // Purple
    if (standing >= 500) return '#3a86ff'; // Blue
    if (standing >= 300) return '#06d6a0'; // Green
    if (standing >= 100) return '#ffbe0b'; // Yellow
    return '#ef476f'; // Red
  };

  const getQuestProgress = (questType: string): number => {
    if (!questProgress) return 0;

    switch (questType) {
      case 'Combat':
        return questProgress.combat || 0;
      case 'EliteCombat':
        return questProgress.eliteCombat || 0;
      case 'Crafting':
        return questProgress.crafting || 0;
      default:
        return 0;
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content town-modal" onClick={(e) => e.stopPropagation()}>
          <p>Loading town details...</p>
        </div>
      </div>
    );
  }

  if (error && !town) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content town-modal" onClick={(e) => e.stopPropagation()}>
          <p className="error">{error}</p>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  if (!town) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content town-modal" onClick={(e) => e.stopPropagation()}>
        <div className="town-header">
          <h2>
            {getTownIcon(town.town.townType)} {town.town.name}
          </h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="town-description">
          <p>{town.town.description}</p>
          <p className="town-type">Type: {town.town.townType}</p>
        </div>

        <div className="town-progress">
          <div className="progress-header">
            <h3>Town Progress</h3>
            {town.progress.canFastTravel && onFastTravel && (
              <button className="fast-travel-btn" onClick={handleFastTravel}>
                🚀 Fast Travel
              </button>
            )}
          </div>

          {!town.progress.isLiberated ? (
            <div className="liberation-status">
              <p className="not-liberated">⚔️ Town is under enemy control!</p>
              <p>Defeat <strong>{town.town.bossName}</strong> to liberate this town.</p>

              {town.bossSpawns && town.bossSpawns.length > 0 ? (
                <div className="boss-battles-available">
                  <p style={{ marginTop: '10px', fontWeight: 'bold', color: '#ff6b6b' }}>
                    Boss Battle Available!
                  </p>
                  {town.bossSpawns.map((boss: any, idx: number) => (
                    <div key={idx} style={{
                      background: 'rgba(255, 107, 107, 0.1)',
                      padding: '10px',
                      borderRadius: '8px',
                      marginTop: '8px',
                      border: '2px solid #ff6b6b'
                    }}>
                      <strong>{boss.monsterName}</strong> (Level {boss.monsterLevel} - {boss.isDifficulty})
                    </div>
                  ))}
                  <p style={{ marginTop: '8px', fontSize: '0.9em', fontStyle: 'italic' }}>
                    Return to the world map and battle this enemy to free the town!
                  </p>
                </div>
              ) : (
                <p style={{ marginTop: '10px', color: '#ffbe0b' }}>
                  ⚠️ No boss spawns available yet. Explore nearby tiles to trigger boss encounters!
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="standing-bar">
                <div className="standing-label">
                  Standing: {town.progress.standing} / 1000 (Level {town.progress.level})
                </div>
                <div className="standing-bar-bg">
                  <div
                    className="standing-bar-fill"
                    style={{
                      width: `${(town.progress.standing / 1000) * 100}%`,
                      backgroundColor: getStandingColor(town.progress.standing)
                    }}
                  />
                </div>
              </div>

              <div className="level-bonuses">
                <p>
                  Next Bonus: {town.progress.level < 9 ? `Level ${town.progress.level + 1}` : 'MAX LEVEL!'}
                  {town.progress.level < 9 && ` (${100 * (town.progress.level + 1) - town.progress.standing} standing needed)`}
                </p>
              </div>
            </>
          )}
        </div>

        {town.progress.isLiberated && (
          <div className="town-quests">
            <h3>Available Quests</h3>
            <div className="quest-list">
              {town.availableQuests.map((quest: Quest, idx: number) => {
                const currentProgress = getQuestProgress(quest.type);
                const hasEnoughProgress = currentProgress >= quest.requiredCount;

                return (
                  <div key={idx} className="quest-card">
                    <div className="quest-header">
                      <span className="quest-type">{quest.type}</span>
                      <span className="quest-reward">+{quest.standingReward} Standing</span>
                    </div>
                    <p className="quest-description">{quest.description}</p>
                    {quest.type !== 'Gathering' && quest.type !== 'Donation' && (
                      <p className="quest-progress" style={{
                        color: hasEnoughProgress ? '#06d6a0' : '#ffbe0b',
                        fontWeight: 'bold',
                        marginTop: '5px'
                      }}>
                        Progress: {currentProgress}/{quest.requiredCount}
                        {hasEnoughProgress && ' ✓'}
                      </p>
                    )}
                    <button
                      className="complete-quest-btn"
                      onClick={() => handleCompleteQuest(quest)}
                      disabled={completingQuest}
                    >
                      {completingQuest ? 'Completing...' : 'Complete Quest'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="town-bonuses">
          <h3>Town Bonuses</h3>
          <div className="bonus-grid">
            {[1, 2, 3].map(slot => {
              const slotBonuses = town.bonuses.filter((b: Bonus) => b.slot === slot);
              return (
                <div key={slot} className="bonus-slot">
                  <h4>Slot {slot}</h4>
                  {slotBonuses.map((bonus: Bonus) => (
                    <div
                      key={`${bonus.slot}-${bonus.tier}`}
                      className={`bonus-tier ${bonus.isActive ? 'active' : 'locked'}`}
                    >
                      <div className="bonus-header">
                        <span className="bonus-name">{bonus.bonusName}</span>
                        <span className="bonus-level">Lv {bonus.requiredLevel}</span>
                      </div>
                      <p className="bonus-description">{bonus.description}</p>
                      {bonus.isLocked && (
                        <span className="locked-badge">🔒 Locked</span>
                      )}
                      {bonus.isActive && (
                        <span className="active-badge">✓ Active</span>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        <div className="town-actions">
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default TownDetailsModal;
